import { SimpleGrid } from "@chakra-ui/react"
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next"
import { UserConfig } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import Head from "next/head"
import { useRouter } from "next/router"
import { namespaces } from "../../../../domains.config"
import nextI18nextConfig from "../../../../next-i18next.config"
import { Category } from "../../../../src/api-clients/product-api-v1"
import { categoryTreeDepth, pages } from "../../../../src/common/constants/constants"
import { getAllSeriesCategories } from "../../../../src/common/hooks/queries"
import { useBrandedTranslation } from "../../../../src/common/hooks/useBrandedTranslation"
import { formatCategoryImages } from "../../../../src/common/utils/category-utils"
import { getBaseURLwithLocale, getFullURL } from "../../../../src/common/utils/common-utils"
import { prepareStructuredMicrodataForBreadcrumbs } from "../../../../src/common/utils/schema-org-utils"
import { SeriesOverviewCard } from "../../../../src/components/features/series/SeriesOverview/SeriesOverviewCard"
import Breadcrumbs from "../../../../src/components/shared/Breadcrumbs/Breadcrumbs"
import { SectionTitle } from "../../../../src/components/shared/SectionTitle"
import { CustomPageProps } from "../../../_app"

type Props = InferGetServerSidePropsType<typeof getServerSideProps> & CustomPageProps

const SeriesOverviewPage = ({ seriesCategories, indexEnabled, host }: Props) => {
  const { t } = useBrandedTranslation()
  const crumbs = [{ name: t("nav-series"), href: null }]
  const router = useRouter()
  const { locale, asPath } = router
  const fullURL = getFullURL(host, locale, asPath)
  const baseURLWithLocale = getBaseURLwithLocale(host, locale)

  const breadcrumbsSchemaMarkup = prepareStructuredMicrodataForBreadcrumbs(crumbs, baseURLWithLocale?.href)

  return (
    <>
      <Head>
        <title>{t("nav-series")}</title>
        <meta name="robots" key="meta-robots" content={indexEnabled ? "index, follow" : "noindex, nofollow"} />
        {indexEnabled && <link rel="canonical" href={fullURL?.href} />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbsSchemaMarkup) }}
        />
      </Head>

      <Breadcrumbs crumbs={crumbs} />

      <SectionTitle title={t("nav-series")} as="h1" />
      <SimpleGrid columns={[1, 1, 2, 3]} spacing="6">
        {seriesCategories?.map((series) => (
          <SeriesOverviewCard
            key={series.id}
            headline={series.name}
            description={series.slogan}
            link={`/${pages.SERIES}/${series.id}`}
            image={formatCategoryImages(series.images)?.[0] || {}}
          />
        ))}
      </SimpleGrid>
    </>
  )
}

export const getServerSideProps = async ({ locale, params }: GetServerSidePropsContext) => {
  const brand = params?.brand?.toString()

  try {
    // Do a parallel request for all API calls
    const [seriesCategoriesRes, translations] = await Promise.all([
      getAllSeriesCategories(locale, brand, categoryTreeDepth.ONE),
      serverSideTranslations(locale, namespaces, nextI18nextConfig as UserConfig),
    ])

    return {
      props: {
        ...translations,
        seriesCategories: (seriesCategoriesRes?.data?.subCategories as Category[]) ?? null,
      },
    }
  } catch (e) {
    return {
      redirect: {
        destination: `/${locale}/404`,
        permanent: false,
      },
    }
  }
}

export default SeriesOverviewPage
